/**
 * Extracts a human-readable error message from a Reznor error response.
 *
 * Per `block_list_feature_openapi.yaml` the Reznor API uses three error
 * shapes (see #/components/schemas):
 *
 *   SimpleErrorResponse:    { error: string }
 *   ValidationErrorResponse: { errors: { <field>: string[] } }
 *   BatchErrorResponse:     { error: string, details: [{ index, error, invalid_attribute? }] }
 *
 * For BatchErrorResponse, when details entries carry `invalid_attribute.attribute_type`
 * (the unique-constraint / already-blocklisted case), the message lists the
 * conflicting types rather than surfacing the generic top-level error string.
 *
 * This helper covers all three and returns `undefined` when the payload
 * doesn't match any of them — callers should fall back to their own
 * generic message in that case.
 */

type DetailEntry = {
  error?: string;
  index?: number;
  invalid_attribute?: { attribute_type?: string };
};

export const extractReznorError = (data: unknown): string | undefined => {
  if (!data || typeof data !== 'object') return undefined;
  const payload = data as Record<string, unknown>;

  // BatchErrorResponse with invalid_attribute — list conflicting attribute types
  if (Array.isArray(payload.details) && payload.details.length > 0) {
    const details = payload.details as DetailEntry[];
    const blocklisted = details
      .map((d) => d.invalid_attribute?.attribute_type)
      .filter((t): t is string => typeof t === 'string' && t.length > 0);

    if (blocklisted.length > 0) {
      const prefix =
        blocklisted.length === 1
          ? 'A record you are trying to add is already blocklisted'
          : 'The records you are trying to add are already blocklisted';
      const otherErrors = details
        .filter(
          (d) =>
            !d.invalid_attribute?.attribute_type &&
            typeof d.error === 'string' &&
            d.error.length > 0
        )
        .map((d) => d.error as string);
      return [`${prefix}: ${blocklisted.join(', ')}`, ...otherErrors].join(
        '. '
      );
    }
  }

  // SimpleErrorResponse / BatchErrorResponse — single `error` string
  if (typeof payload.error === 'string' && payload.error.length > 0) {
    return payload.error;
  }

  // ValidationErrorResponse — hash of field → string[]
  if (
    payload.errors &&
    typeof payload.errors === 'object' &&
    !Array.isArray(payload.errors)
  ) {
    const errorsByField = payload.errors as Record<string, unknown>;
    const firstField = Object.keys(errorsByField)[0];
    if (firstField) {
      const fieldErrors = errorsByField[firstField];
      if (
        Array.isArray(fieldErrors) &&
        typeof fieldErrors[0] === 'string' &&
        fieldErrors[0].length > 0
      ) {
        return `${firstField}: ${fieldErrors[0]}`;
      }
    }
  }

  return undefined;
};
